import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import Grid from '@material-ui/core/Grid'
import { Typography } from '@material-ui/core'
import { Controller, useForm } from 'react-hook-form'
import { Alert } from '@material-ui/lab'
import SydLabel, { hookFormErrorAdapter } from '../../../../commonDesign/SydLabel'
import SydInput from '../../../../commonDesign/SydInput'
import OpalCard from '../../../../opal/OpalCard'
import ConnectionFormField from '../../../denali/Integrator/ConnectionsList/CreateConnectionDialog/ConnectionFormField'
import FadeIn from '../../../../molecules/Transitions/FadeIn'
import FlexRow from '../../../../molecules/FlexRow'
import SydButton from '../../../../commonDesign/Button'
import { connections } from '../../../../../api/integrations'
import { decipherError } from '../../../../../utils/decipherError'
import InitiateOAuthFlowButton
  from '../../../denali/Integrator/ConnectionsList/CreateConnectionDialog/InitiateOAuthFlowButton'

const useSubmitter = (form, onComplete, request) => {
  const [processing, setProcessing] = useState(false)
  const [error, setError] = useState(null)
  const { handleSubmit } = form
  const { mutateAsync: resolveRequest } = connections.useResolveIntegrationConnectionRequest()
  const onSubmit = useCallback(async (formData) => {
    const command = {
      connectionRequestId: request.connectionRequestId,
      connectionArgs: formData.connectionArgs
    }

    try {
      setError(null)
      setProcessing(true)

      const result = await resolveRequest(command)
      if (result?.statusCode === 500) {
        setError('Something unexpected happened while resolving the integration connection request')
        return
      }
      onComplete(result)
    } catch (err) {
      setError(decipherError(err))
    } finally {
      setProcessing(false)
    }
  }, [setProcessing, onComplete, setError, resolveRequest, request])

  const submitter = useCallback(async (e) => {
    const onValid = async (form) => {
      await onSubmit(form)
    }
    const onInvalid = (errors) => {
      console.error(errors)
    }

    const handler = handleSubmit(onValid, onInvalid)
    await handler(e)
  }, [handleSubmit, onSubmit])

  return {
    submitter,
    processing,
    error
  }
}

function ConnectionRequestForm ({ request, template, onComplete }) {
  const form = useForm({
    defaultValues: {
      connectionRequestId: request.connectionRequestId,
      commandArgs: {}
    },
    mode: 'onChange'
  })
  const disabled = useMemo(() => {
    return request.isExpired || request.isResolved || request.existingConnectionCount > 0
  }, [request])

  const authRequiredFields = template?.type === 'oauth' && template?.fields
    ? template?.fields.filter(f => f.type !== 'oauth')
    : []
  const authRequiredFieldValuesObj = {}
  authRequiredFields.forEach(field => {
    authRequiredFieldValuesObj[field.name] = form.watch(`connectionArgs.${field.name}`)
  })

  const hasEmptyRequiredFields = template?.type === 'oauth' && template?.fields ? template?.fields.filter(f => f.type !== 'oauth').some((field) => {
    const isRequired = field.rules?.required?.value === true
    const fieldValue = authRequiredFieldValuesObj[field.name]
    return isRequired && (!fieldValue || fieldValue === '' || fieldValue === undefined)
  }) : false

  const { submitter, processing, error } = useSubmitter(form, onComplete, request)

  return (
    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Typography variant='h3'>{request.description}</Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <SydLabel label='Connection Code'>
          <SydInput size='sm' value={request.code} disabled />
        </SydLabel>
      </Grid>
      <Grid item xs={12} md={6}>
        <SydLabel label='Name'>
          <SydInput size='sm' value={request.displayName} disabled />
        </SydLabel>
      </Grid>
      <Grid item xs={12}>
        <SydLabel label='Description'>
          <textarea value={request.description} disabled placeholder='No Description Provided' style={{ width: '100%', minHeight: '100px' }} />
        </SydLabel>
      </Grid>
      <Grid item xs={12}>
        <OpalCard size='md'>
          {disabled ? (
            <>
              {request.isResolved ? (
                <Alert severity='success'>Request Complete</Alert>
              ) : request.existingConnectionCount ? (
                <Alert severity='success'>Request Complete</Alert>
              ) : request.isExpired ? (
                <Alert severity='error'>This request is expired</Alert>
              ) : null}
            </>
          ) : (
            <FadeIn>
              {template.type === 'oauth' ? (
                <Grid container spacing={1}>
                  {(template.fields || []).map(field => (
                    field.type !== 'oauth' ? (
                      <Grid item xs={12} key={`field_${field.name}`}>
                        <Controller
                          control={form.control}
                          name={`connectionArgs.${field.name}`}
                          rules={field.rules}
                          render={(f) => (
                            <SydLabel label={field.label || field.name} error={hookFormErrorAdapter(form, f.fieldState)}>
                              <ConnectionFormField type={field.type} {...f.field} disabled={processing} />
                            </SydLabel>
                          )}
                        />
                      </Grid>
                    ) : (
                      <Grid item xs={12} key={`field_${field.name}`}>
                        <Controller
                          control={form.control}
                          name='connectionArgs.authorizationId'
                          rules={{
                            required: { value: true, message: 'Required' }
                          }}
                          render={(f) => (
                            <SydLabel label='' error={hookFormErrorAdapter(form, f.fieldState)}>
                              <InitiateOAuthFlowButton
                                config={template.config}
                                label={template.label}
                                value={f.field.value}
                                onChange={f.field.onChange}
                                authRequiredFieldValues={authRequiredFieldValuesObj}
                                disabled={hasEmptyRequiredFields}
                              />
                            </SydLabel>
                          )}
                        />
                      </Grid>
                    )
                  ))}
                </Grid>
              ) : (
                <Grid container spacing={1}>
                  {(template.fields || []).map(field => (
                    <Grid item xs={12} key={`field_${field.name}`}>
                      <Controller
                        control={form.control}
                        name={`connectionArgs.${field.name}`}
                        rules={field.rules}
                        render={(f) => (
                          <SydLabel label={field.label || field.name} error={hookFormErrorAdapter(form, f.fieldState)}>
                            <ConnectionFormField type={field.type} {...f.field} disabled={processing} />
                          </SydLabel>
                        )}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
            </FadeIn>
          )}
        </OpalCard>
      </Grid>
      <Grid item xs={12}>
        <FlexRow className='justify-end gap-md'>
          {error ? <Alert severity='error' className='flex-1'>{error}</Alert> : null}
          <SydButton size='md' variant='primary' onClick={submitter} processing={processing} disabled={disabled}>Submit</SydButton>
        </FlexRow>
      </Grid>
    </Grid>
  )
}

ConnectionRequestForm.propTypes = {
  request: PropTypes.shape({
    connectionRequestId: PropTypes.string,
    displayName: PropTypes.string,
    code: PropTypes.string,
    description: PropTypes.string,
    domain: PropTypes.string,
    isExpired: PropTypes.bool,
    isResolved: PropTypes.bool,
    existingConnectionCount: PropTypes.number
  }),
  template: PropTypes.object,
  onComplete: PropTypes.func
}

export default ConnectionRequestForm
